import type { EmailTemplate } from './template-utils';

/** Sentinel ids for the two synthetic buckets in the tag column. */
export const ALL_TAG_ID = '__all__';
export const UNTAGGED_TAG_ID = '__untagged__';

export interface TagFilterEntry {
  /** Tag id, or one of the sentinel ids above. */
  id: string;
  /** Display label ("All", a tag name, or "Untagged"). */
  name: string;
  /** Number of distinct templates in this bucket. */
  count: number;
}

function hasNoTags(template: EmailTemplate): boolean {
  return (
    !template.template_tag_relations ||
    template.template_tag_relations.length === 0
  );
}

/**
 * Build the left-pane tag list purely from the loaded templates — no count
 * query, no dependency on the template_tag_stats view.
 *
 *  - `All` always first, count = total templates.
 *  - one entry per tag that has >= 1 template (0-count tags can't appear
 *    because we only iterate tags actually present on templates), counts are
 *    DISTINCT templates per tag, sorted by tag name A->Z.
 *  - `Untagged` last, only when >= 1 untagged template exists.
 */
export function deriveTagEntries(templates: EmailTemplate[]): TagFilterEntry[] {
  const entries: TagFilterEntry[] = [
    { id: ALL_TAG_ID, name: 'All', count: templates.length },
  ];

  // tagId -> { name, set of distinct template ids }
  const byTag = new Map<string, { name: string; ids: Set<string> }>();
  let untaggedCount = 0;

  for (const template of templates) {
    if (hasNoTags(template)) {
      untaggedCount += 1;
      continue;
    }
    // Dedupe tags within a single template so duplicate relation rows
    // don't inflate the per-tag count.
    const seen = new Set<string>();
    for (const relation of template.template_tag_relations ?? []) {
      const tag = relation.template_tags;
      if (!tag || !tag.id) continue;
      if (seen.has(tag.id)) continue;
      seen.add(tag.id);

      const bucket = byTag.get(tag.id);
      if (bucket) {
        bucket.ids.add(template.id);
      } else {
        byTag.set(tag.id, { name: tag.name, ids: new Set([template.id]) });
      }
    }
  }

  const tagEntries = Array.from(byTag.entries())
    .map(([id, { name, ids }]) => ({ id, name, count: ids.size }))
    .filter((entry) => entry.count > 0)
    .sort((a, b) => a.name.localeCompare(b.name));

  entries.push(...tagEntries);

  if (untaggedCount > 0) {
    entries.push({
      id: UNTAGGED_TAG_ID,
      name: 'Untagged',
      count: untaggedCount,
    });
  }

  return entries;
}

/**
 * Filter the template list for the currently-selected tag bucket.
 *  - ALL_TAG_ID -> everything
 *  - UNTAGGED_TAG_ID -> templates with no tag relations
 *  - any other id -> templates carrying that tag
 */
export function filterTemplatesByTag(
  templates: EmailTemplate[],
  tagId: string
): EmailTemplate[] {
  if (tagId === ALL_TAG_ID) return templates;
  if (tagId === UNTAGGED_TAG_ID) return templates.filter(hasNoTags);
  return templates.filter((template) =>
    template.template_tag_relations?.some(
      (relation) => relation.template_tags?.id === tagId
    )
  );
}
